/**
 * The feel layer: particles, decals, hitstop, floating numbers.
 *
 * None of this touches the simulation. It is driven entirely by the event list
 * the sim publishes each tick, which means you can rip the whole thing out and
 * the game still plays identically — and it means the headless harness never
 * pays for any of it.
 *
 * Hitstop is the single highest-value effect in here. A 60–110 ms freeze scaled
 * to damage is the difference between "a number went down" and "that landed".
 */

import type { Texture } from 'pixi.js';
import { Container, Sprite } from 'pixi.js';
import type { TextureBank } from './textures.ts';
import type { Camera } from './camera.ts';
import { C, mix } from '../data/palette.ts';
import { clamp01 } from '../core/math.ts';

const MAX_PARTICLES = 2400;
const MAX_DECALS = 900;

interface Particle {
  sprite: Sprite;
  x: number;
  y: number;
  vx: number;
  vy: number;
  /** Height above ground, so blood arcs and embers rise. */
  z: number;
  vz: number;
  life: number;
  maxLife: number;
  rot: number;
  vrot: number;
  size: number;
  endSize: number;
  alpha0: number;
  gravity: number;
  drag: number;
  additive: boolean;
  active: boolean;
}

interface Decal {
  sprite: Sprite;
  life: number;
  maxLife: number;
  alpha0: number;
}

export interface FloatingNumber {
  x: number;
  y: number;
  vy: number;
  life: number;
  text: string;
  color: number;
  scale: number;
}

export class Fx {
  readonly particleLayer = new Container();
  readonly decalLayer = new Container();
  readonly additiveLayer = new Container();

  private pool: Particle[] = [];
  private decals: Decal[] = [];
  private decalCursor = 0;
  readonly numbers: FloatingNumber[] = [];

  /** Seconds of simulation freeze remaining. */
  hitstop = 0;
  /** Slow-motion factor, 1 = normal. */
  slowmo = 1;
  private slowmoT = 0;

  showNumbers = true;

  constructor(private bank: TextureBank) {
    this.particleLayer.eventMode = 'none';
    this.decalLayer.eventMode = 'none';
    this.additiveLayer.eventMode = 'none';
    this.additiveLayer.blendMode = 'add';

    for (let i = 0; i < MAX_PARTICLES; i++) {
      const sprite = new Sprite(bank.blob);
      sprite.anchor.set(0.5);
      sprite.visible = false;
      this.particleLayer.addChild(sprite);
      this.pool.push({
        sprite,
        x: 0,
        y: 0,
        vx: 0,
        vy: 0,
        z: 0,
        vz: 0,
        life: 0,
        maxLife: 1,
        rot: 0,
        vrot: 0,
        size: 1,
        endSize: 1,
        alpha0: 1,
        gravity: 0,
        drag: 1,
        additive: false,
        active: false,
      });
    }
  }

  private take(additive: boolean): Particle | null {
    // Linear scan from a rotating cursor: cheap, and reuse order is stable.
    for (let n = 0; n < 48; n++) {
      const p = this.pool[this.cursor];
      this.cursor = (this.cursor + 1) % this.pool.length;
      if (!p.active) {
        if (additive !== p.additive) {
          p.additive = additive;
          (additive ? this.additiveLayer : this.particleLayer).addChild(p.sprite);
        }
        return p;
      }
    }
    return null;
  }
  private cursor = 0;

  emit(opts: {
    texture?: Texture;
    x: number;
    y: number;
    z?: number;
    vx?: number;
    vy?: number;
    vz?: number;
    life: number;
    size: number;
    endSize?: number;
    color: number;
    alpha?: number;
    gravity?: number;
    drag?: number;
    rot?: number;
    vrot?: number;
    additive?: boolean;
  }): void {
    const p = this.take(opts.additive ?? false);
    if (!p) return;
    p.active = true;
    p.sprite.texture = opts.texture ?? this.bank.blob;
    p.sprite.visible = true;
    p.sprite.tint = opts.color;
    p.x = opts.x;
    p.y = opts.y;
    p.z = opts.z ?? 0;
    p.vx = opts.vx ?? 0;
    p.vy = opts.vy ?? 0;
    p.vz = opts.vz ?? 0;
    p.life = opts.life;
    p.maxLife = opts.life;
    p.size = opts.size;
    p.endSize = opts.endSize ?? opts.size;
    p.alpha0 = opts.alpha ?? 1;
    p.gravity = opts.gravity ?? 0;
    p.drag = opts.drag ?? 1;
    p.rot = opts.rot ?? 0;
    p.vrot = opts.vrot ?? 0;
  }

  /** A persistent ground mark. Decals are recycled oldest-first. */
  decal(texture: Texture, x: number, y: number, size: number, color: number, alpha: number, rot = 0, life = 220): void {
    let d: Decal;
    if (this.decals.length < MAX_DECALS) {
      const sprite = new Sprite(texture);
      sprite.anchor.set(0.5);
      this.decalLayer.addChild(sprite);
      d = { sprite, life, maxLife: life, alpha0: alpha };
      this.decals.push(d);
    } else {
      d = this.decals[this.decalCursor];
      this.decalCursor = (this.decalCursor + 1) % this.decals.length;
      d.sprite.texture = texture;
      d.life = life;
      d.maxLife = life;
      d.alpha0 = alpha;
    }
    d.sprite.visible = true;
    d.sprite.tint = color;
    d.sprite.alpha = alpha;
    d.sprite.rotation = rot;
    // Decal positions are stored in world tiles on the sprite itself; the layer
    // is transformed as a whole by the camera.
    d.sprite.x = x;
    d.sprite.y = y;
    d.sprite.width = size;
    d.sprite.height = size;
  }

  floatNumber(x: number, y: number, text: string, color: number, scale = 1): void {
    if (!this.showNumbers) return;
    if (this.numbers.length > 90) this.numbers.shift();
    this.numbers.push({ x, y, vy: -1.5, life: 0.85, text, color, scale });
  }

  freeze(seconds: number): void {
    this.hitstop = Math.max(this.hitstop, seconds);
  }

  slowMotion(factor: number, seconds: number): void {
    this.slowmo = Math.min(this.slowmo, factor);
    this.slowmoT = Math.max(this.slowmoT, seconds);
  }

  update(dt: number, camera: Camera): void {
    if (this.hitstop > 0) this.hitstop = Math.max(0, this.hitstop - dt);
    if (this.slowmoT > 0) {
      this.slowmoT -= dt;
      if (this.slowmoT <= 0) this.slowmo = 1;
    }

    const z = camera.effectiveZoom;

    for (const p of this.pool) {
      if (!p.active) continue;
      p.life -= dt;
      if (p.life <= 0) {
        p.active = false;
        p.sprite.visible = false;
        continue;
      }
      p.vz -= p.gravity * dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.z += p.vz * dt;
      if (p.z < 0) {
        p.z = 0;
        p.vz *= -0.32;
        p.vx *= 0.55;
        p.vy *= 0.55;
      }
      const d = Math.pow(p.drag, dt * 60);
      p.vx *= d;
      p.vy *= d;
      p.rot += p.vrot * dt;

      const t = 1 - p.life / p.maxLife;
      const size = p.size + (p.endSize - p.size) * t;
      const s = p.sprite;
      s.x = camera.worldToScreenX(p.x);
      s.y = camera.worldToScreenY(p.y) - p.z * z;
      s.width = size * z;
      s.height = size * z;
      s.rotation = p.rot;
      s.alpha = p.alpha0 * (1 - t * t);
    }

    for (const d of this.decals) {
      if (!d.sprite.visible) continue;
      d.life -= dt;
      if (d.life <= 0) {
        d.sprite.visible = false;
        continue;
      }
      // Fade only in the last quarter of life so the field stays littered.
      const t = d.life / d.maxLife;
      d.sprite.alpha = d.alpha0 * clamp01(t * 4);
    }

    for (let i = this.numbers.length - 1; i >= 0; i--) {
      const n = this.numbers[i];
      n.life -= dt;
      n.y += n.vy * dt;
      n.vy *= 0.94;
      if (n.life <= 0) this.numbers.splice(i, 1);
    }
  }

  /** The decal layer lives in world space; the camera transforms it wholesale. */
  syncDecalLayer(camera: Camera): void {
    const z = camera.effectiveZoom;
    this.decalLayer.scale.set(z);
    this.decalLayer.x = camera.offsetX;
    this.decalLayer.y = camera.offsetY;
    void z;
  }

  clear(): void {
    for (const p of this.pool) {
      p.active = false;
      p.sprite.visible = false;
    }
    for (const d of this.decals) d.sprite.visible = false;
    this.numbers.length = 0;
    this.hitstop = 0;
    this.slowmo = 1;
  }

  // ── Canned effects ───────────────────────────────────────────────────────

  bloodBurst(x: number, y: number, dx: number, dy: number, power: number, cold: boolean): void {
    const n = Math.min(14, 3 + Math.round(power * 0.14));
    const base = cold ? 0x5f6f8c : C.oxblood;
    for (let i = 0; i < n; i++) {
      const a = Math.atan2(dy, dx) + (i / n - 0.5) * 1.5;
      const sp = 2.2 + (i % 4) * 1.5;
      this.emit({
        texture: this.bank.blood,
        x,
        y,
        z: 0.25,
        vx: Math.cos(a) * sp,
        vy: Math.sin(a) * sp,
        vz: 1.4 + (i % 3) * 0.9,
        life: 0.5 + (i % 3) * 0.22,
        size: 0.16 + (i % 3) * 0.06,
        endSize: 0.1,
        color: i % 4 === 0 ? mix(base, 0x000000, 0.35) : base,
        alpha: 0.92,
        gravity: 9,
        drag: 0.93,
        rot: a,
        vrot: (i % 2 ? 1 : -1) * 4,
      });
    }
  }

  sparks(x: number, y: number, dx: number, dy: number, count = 6): void {
    for (let i = 0; i < count; i++) {
      const a = Math.atan2(dy, dx) + (i / count - 0.5) * 2.2;
      const sp = 3.5 + (i % 3) * 2.4;
      this.emit({
        texture: this.bank.spark,
        x,
        y,
        z: 0.3,
        vx: Math.cos(a) * sp,
        vy: Math.sin(a) * sp,
        vz: 1.2,
        life: 0.22 + (i % 3) * 0.08,
        size: 0.22,
        endSize: 0.05,
        color: i % 2 ? C.goldPale : 0xffffff,
        alpha: 1,
        gravity: 6,
        drag: 0.9,
        rot: a,
        additive: true,
      });
    }
  }

  dust(x: number, y: number, amount = 4, color = 0x4a443b): void {
    for (let i = 0; i < amount; i++) {
      const a = (i / amount) * Math.PI * 2;
      this.emit({
        texture: this.bank.smoke,
        x,
        y,
        z: 0.05,
        vx: Math.cos(a) * 0.5,
        vy: Math.sin(a) * 0.5,
        vz: 0.35,
        life: 0.7 + (i % 3) * 0.2,
        size: 0.35,
        endSize: 1.1,
        color,
        alpha: 0.28,
        drag: 0.95,
        rot: a,
        vrot: 0.6,
      });
    }
  }

  embers(x: number, y: number, count = 8, spread = 1.5): void {
    for (let i = 0; i < count; i++) {
      const a = (i / count) * Math.PI * 2;
      this.emit({
        texture: this.bank.ember,
        x: x + Math.cos(a) * spread * 0.3,
        y: y + Math.sin(a) * spread * 0.3,
        z: 0.2,
        vx: Math.cos(a) * spread,
        vy: Math.sin(a) * spread,
        vz: 2.4 + (i % 3),
        life: 0.9 + (i % 4) * 0.3,
        size: 0.16,
        endSize: 0.04,
        color: i % 3 === 0 ? C.goldPale : C.emberLight,
        alpha: 1,
        gravity: 2.2,
        drag: 0.96,
        additive: true,
      });
    }
  }

  smokePlume(x: number, y: number, count = 5, color = 0x2a2724): void {
    for (let i = 0; i < count; i++) {
      this.emit({
        texture: this.bank.smoke,
        x: x + (i % 3) * 0.2 - 0.2,
        y: y + ((i / 3) | 0) * 0.2 - 0.2,
        z: 0.1,
        vx: (i % 2 ? 0.3 : -0.3) * 0.6,
        vy: -0.25,
        vz: 0.9 + (i % 3) * 0.3,
        life: 1.4 + (i % 3) * 0.5,
        size: 0.6,
        endSize: 2.4,
        color,
        alpha: 0.32,
        drag: 0.97,
        vrot: 0.35,
      });
    }
  }

  shockRing(x: number, y: number, radius: number, color: number, life = 0.42): void {
    this.emit({
      texture: this.bank.ring,
      x,
      y,
      z: 0.02,
      life,
      size: radius * 0.35,
      endSize: radius * 2.1,
      color,
      alpha: 0.85,
      additive: true,
    });
  }
}
